import React from 'react'
import { Flex, Spin } from 'antd'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { SeedOrderStatus } from 'modules/domain/seedOrders/types'
import {
  seedInventoryNotesEmptyStyle,
  seedInventoryNotesLoadingStyle,
  seedInventoryNotesRootStyle,
  historyDateStyle,
  historyMarkerStyle,
  historyMetaStyle,
  historyActionStyle,
  historyContentStyle,
  customTimelineStyle,
  customRailStyle,
  historyEntryRowStyle,
  historyDiffContainerStyle,
  historyDiffRowStyle,
  historyDiffFieldStyle,
  historyDiffValueStyle,
  historyTimelineContainerStyle,
  historyArrowStyle,
  PedigreeLabel,
  OldValueBubble,
  NewValueBubble,
  DeletedValueBubble,
  StrikethroughValue,
} from '../../../pages/SeedTracker/styles'
import { StatusLabel } from '../../../pages/SeedOrders/styles'

type HistoryEntry = {
  created_at: string
  author_name: string
  operation: string
  action: string
  collection: string
  diff: Array<{ field_name: string; old_value: string; new_value: string }>
}

type HistoryDisplayProps = {
  entries: HistoryEntry[]
  loading: boolean
  empty?: boolean
  fieldNameMap?: Record<string, string>
  fieldFormatter?: (fieldName: string, value: string, isOldValue: boolean) => React.ReactNode
}

// Status background color function
function getStatusBackgroundColor(status: SeedOrderStatus): string {
  switch (status) {
    case SeedOrderStatus.Pending:
      return '#037BFF'
    case SeedOrderStatus.Approved:
      return '#2E7D32'
    case SeedOrderStatus.InTransit:
      return '#F57C00'
    case SeedOrderStatus.Completed:
      return '#20B02E'
    case SeedOrderStatus.Cancelled:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

// Default pedigree background color function
function getPedigreeBackgroundColor(pedigree: SeedPedigree): string {
  switch (pedigree) {
    case SeedPedigree.Certified:
      return '#20B02E'
    case SeedPedigree.Registered:
      return '#5C3700'
    case SeedPedigree.Foundation:
      return '#FFAB2E'
    case SeedPedigree.Select:
      return '#037BFF'
    case SeedPedigree.Breeder:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

// Default field formatter
export function defaultFieldFormatter(fieldName: string, value: string, isOldValue: boolean): React.ReactNode {
  if (!value) return ''

  const formattedValue = (() => {
    // MT fields (2 decimal places)
    if (
      [
        'disposed_mt',
        'on_hand_mt',
        'clean_yield_actual_mt',
        'clean_yield_estimate_mt',
        'quantity_mt',
        'origin_current_mt',
        'origin_requested_mt',
        'origin_after_sale_mt',
        'destination_current_mt',
        'destination_requested_mt',
        'destination_after_sale_mt',
      ].includes(fieldName)
    ) {
      const num = parseFloat(value)
      return Number.isNaN(num) ? value : `${num.toFixed(2)} MT`
    }

    // Bu fields (2 decimal places)
    if (
      [
        'gross_yield_actual_bu',
        'gross_yield_estimate_bu',
        'projected_yield_actual_bu',
        'projected_yield_estimate_bu',
      ].includes(fieldName)
    ) {
      const num = parseFloat(value)
      return Number.isNaN(num) ? value : `${num.toFixed(2)} Bu`
    }

    // Percentage fields (0 decimal places)
    if (
      [
        'initial_germ_actual',
        'initial_germ_estimate',
        'final_germ_actual',
        'final_germ_estimate',
        'diseased_actual',
        'diseased_estimate',
      ].includes(fieldName)
    ) {
      const num = parseFloat(value)
      return Number.isNaN(num) ? value : `${num.toFixed(0)} %`
    }

    // Acres fields (0 decimal places)
    if (['planned_seeded_area', 'current_seeded_area_actual', 'current_seeded_area_estimate'].includes(fieldName)) {
      const num = parseFloat(value)
      return Number.isNaN(num) ? value : `${num.toFixed(0)} Acres`
    }

    // Pedigree field
    if (['projected_pedigree', 'seeded_pedigree', 'sold_as_pedigree', 'current_pedigree'].includes(fieldName)) {
      const pedigree = value as SeedPedigree
      if (Object.values(SeedPedigree).includes(pedigree)) {
        return <PedigreeLabel backgroundColor={getPedigreeBackgroundColor(pedigree)}>{pedigree}</PedigreeLabel>
      }
      return value
    }

    // Order status field
    if (fieldName === 'order_status') {
      const status = value as SeedOrderStatus
      if (Object.values(SeedOrderStatus).includes(status)) {
        return <StatusLabel backgroundColor={getStatusBackgroundColor(status)}>{status}</StatusLabel>
      }
      return value
    }

    return value
  })()

  // Wrap non-pedigree/non-status values in bubbles
  if (
    ['projected_pedigree', 'seeded_pedigree', 'sold_as_pedigree', 'current_pedigree'].includes(fieldName) &&
    typeof formattedValue !== 'string'
  ) {
    return formattedValue
  }

  if (fieldName === 'order_status' && typeof formattedValue !== 'string') {
    return formattedValue
  }

  if (isOldValue) {
    return <OldValueBubble>{formattedValue}</OldValueBubble>
  } else {
    return <NewValueBubble>{formattedValue}</NewValueBubble>
  }
}

function formatHistoryDate(dateStr: string | undefined): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? dateStr : format(date, 'dd MMMM yyyy h:mm a')
}

function getLocalizedFieldName(fieldName: string, t: any, fieldNameMap?: Record<string, string>): string {
  if (!fieldNameMap) return fieldName

  const translationKey = fieldNameMap[fieldName]
  return translationKey ? t(`seedTracker:seedTrackerHistoryFields.${translationKey}`) : fieldName
}

const HistoryDisplay: React.FC<HistoryDisplayProps> = ({
  entries,
  loading,
  empty = false,
  fieldNameMap,
  fieldFormatter = defaultFieldFormatter,
}) => {
  const { t } = useTranslation('seedTracker')

  return (
    <Flex vertical gap="small" style={seedInventoryNotesRootStyle}>
      {loading ? (
        <Flex justify="center" style={seedInventoryNotesLoadingStyle}>
          <Spin />
        </Flex>
      ) : empty ? (
        <div style={seedInventoryNotesEmptyStyle}>{t('detail.historyEmpty', 'No history yet.')}</div>
      ) : (
        <div style={customTimelineStyle}>
          <div style={customRailStyle} />
          <div style={historyTimelineContainerStyle}>
            {entries.map((entry, idx) => (
              <div key={idx} style={historyEntryRowStyle}>
                <div style={historyDateStyle}>{formatHistoryDate(entry.created_at)}</div>
                <div style={historyMarkerStyle} />
                <div style={historyContentStyle}>
                  <div style={historyMetaStyle}>
                    {entry.author_name ? <strong>{entry.author_name}</strong> : ''}
                    {entry.author_name ? ' ' : ''}
                    <span style={historyActionStyle}>{entry.action}</span>
                  </div>
                  {entry.diff.length > 0 && (
                    <>
                      {entry.collection === 'notes' ? (
                        <div>
                          {entry.diff.map((diffItem, diffIdx) => (
                            <div key={diffIdx} style={{ marginTop: 8 }}>
                              {entry.operation === 'DELETE' ? (
                                // For deleted notes: show deleted value in red bubble with strikethrough
                                <div>
                                  <DeletedValueBubble>{diffItem.old_value}</DeletedValueBubble>
                                </div>
                              ) : (
                                // For created/updated notes: show old value (strikethrough) and new value (bubble)
                                <>
                                  {diffItem.old_value && (
                                    <div style={{ marginBottom: 4 }}>
                                      <StrikethroughValue>{diffItem.old_value}</StrikethroughValue>
                                    </div>
                                  )}
                                  {diffItem.new_value && (
                                    <div>
                                      <NewValueBubble>{diffItem.new_value}</NewValueBubble>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={historyDiffContainerStyle}>
                          {entry.diff.map((diffItem, diffIdx) => (
                            <div key={diffIdx} style={historyDiffRowStyle}>
                              <div style={historyDiffFieldStyle}>
                                {getLocalizedFieldName(diffItem.field_name, t, fieldNameMap)}:
                              </div>
                              <div style={historyDiffValueStyle}>
                                {fieldFormatter(diffItem.field_name, diffItem.old_value, true)}
                                {diffItem.old_value && diffItem.new_value && <span style={historyArrowStyle}>→</span>}
                                {fieldFormatter(diffItem.field_name, diffItem.new_value, false)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Flex>
  )
}

export default HistoryDisplay
